import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, Plus, X } from "lucide-react";
import api from "@/lib/api";
import ScreenshotUploader from "@/components/ScreenshotUploader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canOverrideDates } from "@/lib/permissions";

export default function NewTradeSheet({ open, onOpenChange, onCreated }) {
  const { user: me } = useAuth();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    ticker: "",
    direction: "long",
    status: "idea",
    entry_price: "",
    entry_range_low: "",
    entry_range_high: "",
    stop_loss: "",
    take_profit_levels: [{ price: "", size_pct: "" }],
    logarithmic: false,
    position_size_pct: "",
    timeframe: "1D",
    confidence: 3,
    setup_criteria: [],
    screenshots: [],
    notes: "",
    created_at_override: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setTpLevel = (i, k, v) => {
    setForm((f) => {
      const levels = [...f.take_profit_levels];
      levels[i] = { ...levels[i], [k]: v };
      return { ...f, take_profit_levels: levels };
    });
  };
  const addTpLevel = () => setForm((f) => ({ ...f, take_profit_levels: [...f.take_profit_levels, { price: "", size_pct: "" }] }));
  const removeTpLevel = (i) => setForm((f) => ({ ...f, take_profit_levels: f.take_profit_levels.filter((_, idx) => idx !== i) }));

  const toggleCriterion = (c) => {
    setForm((f) => ({
      ...f,
      setup_criteria: f.setup_criteria.includes(c) ? f.setup_criteria.filter((x) => x !== c) : [...f.setup_criteria, c],
    }));
  };

  const submit = async () => {
    if (!form.ticker) {
      toast.error("Ticker is required");
      return;
    }
    if (form.stop_loss === "" || form.stop_loss == null) {
      toast.error("Stop is required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      ["entry_price", "entry_range_low", "entry_range_high", "stop_loss", "position_size_pct"].forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] == null ? null : parseFloat(payload[k]);
      });
      payload.take_profit_levels = form.take_profit_levels
        .filter((lv) => lv.price !== "" && lv.price != null)
        .map((lv) => ({
          price: parseFloat(lv.price),
          size_pct: lv.size_pct === "" || lv.size_pct == null ? null : parseFloat(lv.size_pct),
        }));
      // set primary target for legacy compatibility
      payload.take_profit = payload.take_profit_levels[0]?.price ?? null;
      payload.confidence = parseInt(payload.confidence);
      if (payload.created_at_override) {
        payload.created_at_override = new Date(payload.created_at_override).toISOString();
      } else {
        delete payload.created_at_override;
      }
      const { data } = await api.post("/trades", payload);
      toast.success(`${data.ticker} added`);
      onOpenChange(false);
      setForm({ ticker: "", direction: "long", status: "idea", entry_price: "", entry_range_low: "", entry_range_high: "", stop_loss: "", take_profit_levels: [{ price: "", size_pct: "" }], logarithmic: false, position_size_pct: "", timeframe: "1D", confidence: 3, setup_criteria: [], screenshots: [], notes: "", created_at_override: "" });
      onCreated?.(data);
      window.dispatchEvent(new CustomEvent("trades-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create trade");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-[#0a0a0a] border-l border-zinc-900 text-zinc-100 w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading tracking-tight">New Trade</SheetTitle>
          <SheetDescription className="text-zinc-500 text-xs">Log a trade idea or open a position. Position size auto-calculates shares from your account size.</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-6">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Ticker</Label>
              <Input data-testid="trade-ticker-input" value={form.ticker} onChange={(e) => set("ticker", e.target.value.toUpperCase())} placeholder="AAPL" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono uppercase" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Direction</Label>
              <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                <SelectTrigger data-testid="trade-direction-select" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="long">Long</SelectItem>
                  <SelectItem value="short">Short</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Timeframe</Label>
              <Select value={form.timeframe} onValueChange={(v) => set("timeframe", v)}>
                <SelectTrigger data-testid="trade-timeframe-select" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {(settings?.timeframes || ["1H","2H","4H","8H","1D","2D","3D","4D","Weekly","Monthly"]).map((tf) => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Size %</Label>
              <Input data-testid="trade-size-input" type="number" step="0.1" value={form.position_size_pct} onChange={(e) => set("position_size_pct", e.target.value)} placeholder="5" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Entry Range Low</Label>
              <Input data-testid="trade-entry-low-input" type="number" step="0.01" value={form.entry_range_low} onChange={(e) => set("entry_range_low", e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Entry Range High</Label>
              <Input data-testid="trade-entry-high-input" type="number" step="0.01" value={form.entry_range_high} onChange={(e) => set("entry_range_high", e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Stop <span className="text-rose-400">*</span></Label>
              <Input data-testid="trade-stop-input" required type="number" step="0.01" value={form.stop_loss} onChange={(e) => set("stop_loss", e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Take Profit Levels</Label>
              <button type="button" onClick={addTpLevel} data-testid="add-tp-level-btn" className="text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1 font-mono uppercase tracking-wider">
                <Plus className="w-3 h-3" /> Add level
              </button>
            </div>
            <div className="space-y-2">
              {form.take_profit_levels.map((lv, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center">
                  <span className="text-[10px] font-mono text-zinc-500 uppercase w-8">TP{i + 1}</span>
                  <Input
                    type="number" step="0.01" placeholder="Price"
                    value={lv.price}
                    onChange={(e) => setTpLevel(i, "price", e.target.value)}
                    data-testid={`tp-price-${i}`}
                    className="bg-zinc-950 border-zinc-800 rounded-sm font-mono h-8"
                  />
                  <Input
                    type="number" step="1" placeholder="Size %"
                    value={lv.size_pct}
                    onChange={(e) => setTpLevel(i, "size_pct", e.target.value)}
                    data-testid={`tp-size-${i}`}
                    className="bg-zinc-950 border-zinc-800 rounded-sm font-mono h-8"
                  />
                  <button
                    type="button" onClick={() => removeTpLevel(i)}
                    disabled={form.take_profit_levels.length === 1}
                    data-testid={`remove-tp-${i}`}
                    className="text-zinc-600 hover:text-rose-500 disabled:opacity-30 disabled:hover:text-zinc-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
            <Checkbox
              checked={form.logarithmic}
              onCheckedChange={(v) => set("logarithmic", !!v)}
              data-testid="trade-logarithmic-checkbox"
              className="rounded-sm border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
            />
            <span>Logarithmic <span className="text-zinc-500 text-xs font-mono">(log-scale chart)</span></span>
          </label>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Confidence</Label>
            <div className="mt-2 flex gap-1" data-testid="trade-confidence">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => set("confidence", n)} data-testid={`confidence-${n}`}>
                  <Star className={`w-6 h-6 transition-colors ${n <= form.confidence ? "fill-blue-500 text-blue-500" : "text-zinc-700"}`} />
                </button>
              ))}
            </div>
          </div>

          {settings?.setup_criteria_templates?.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Setup Criteria</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {settings.setup_criteria_templates.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100">
                    <Checkbox checked={form.setup_criteria.includes(c)} onCheckedChange={() => toggleCriterion(c)} data-testid={`criterion-${c}`} className="rounded-sm border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
                    {c}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Screenshots</Label>
            <div className="mt-1">
              <ScreenshotUploader
                value={form.screenshots}
                onChange={(next) => set("screenshots", next)}
                pasteScope="global"
                testIdPrefix="trade-screenshot"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Notes</Label>
            <Textarea data-testid="trade-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" />
          </div>

          {canOverrideDates(me) && (
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3">
              <Label className="text-[10px] uppercase tracking-wider text-amber-400 font-mono">Admin · Override Trade Date</Label>
              <Input
                type="datetime-local"
                value={form.created_at_override}
                onChange={(e) => set("created_at_override", e.target.value)}
                data-testid="trade-created-at-override"
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono h-9"
              />
              <p className="text-[10px] text-zinc-600 mt-1">Leave blank to use the current timestamp.</p>
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm border-zinc-800 bg-transparent hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.ticker || form.stop_loss === "" || form.stop_loss == null} data-testid="trade-submit-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Save Trade"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
